<script lang="ts">
  import type { ProductVariant } from '@agapi/mlc-core';
  import { Button } from '@agapi/ui/button';
  import { X } from 'phosphor-svelte';
  import BackInStockForm from '../../pages/product/_components/back-in-stock-form.svelte';
  import * as m from '$lib/parachute/messages';
  import { css } from '@agapi/ui/panda/css';
  import { fade, scale } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';
  import { backInStockDialogStyles } from './back-in-stock-dialog.styles';
    import { variantToProduct } from '../../utils';

  interface BackInStockDialogProps {
    open: boolean;
    product: ProductVariant;
    onClose: () => void;
  }

  let {
    open = false,
    product,
    onClose
  }: BackInStockDialogProps = $props();

  const styles = backInStockDialogStyles();

  // Generate unique IDs for ARIA
  const dialogId = `back-in-stock-dialog-${product.sku}`;
  const titleId = `${dialogId}-title`;
  const descriptionId = `${dialogId}-description`;

  // Focus management
  let dialogElement: HTMLDivElement;
  let previouslyFocusedElement: HTMLElement | null = null;

  $effect(() => {
    if (open) {
      // Store the currently focused element
      previouslyFocusedElement = document.activeElement as HTMLElement;

      // Focus the dialog after a short delay to ensure it's rendered
      setTimeout(() => {
        dialogElement?.focus();
      }, 100);
    } else if (previouslyFocusedElement) {
      // Return focus to the previously focused element
      previouslyFocusedElement.focus();
      previouslyFocusedElement = null;
    }
  });
</script>

{#if open}
  <!-- Backdrop -->
  <div
    class={styles.backdrop}
    onclick={onClose}
    role="presentation"
    in:fade={{ duration: 200, easing: cubicOut }}
    out:fade={{ duration: 150, easing: cubicOut }}
  >
    <!-- Dialog Content -->
    <div
      class={styles.dialogContent}
      onclick={(e) => e.stopPropagation()}
      onkeydown={(e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      tabindex="-1"
      bind:this={dialogElement}
      in:scale={{
        duration: 200,
        start: 0.9,
        opacity: 0,
        easing: cubicOut
      }}
      out:scale={{
        duration: 150,
        start: 0.95,
        opacity: 0,
        easing: cubicOut
      }}
    >
      <!-- Hidden title for screen readers -->
      <h2 id={titleId} class={styles.hiddenTitle}>
        {m.notifyFormTitle()}
      </h2>

      <!-- Hidden description for screen readers -->
      <div id={descriptionId} class={styles.hiddenDescription}>
        {m.outOfStockMessage()}
      </div>

      <!-- Close Button -->
      <div class={styles.closeButtonContainer}>
        <Button
          variant="text"
          size="sm"
          onclick={onClose}
          aria-label="Close dialog"
          css={css.raw({
            p: 1,
            minWidth: 'auto'
          })}
        >
          <X size={24} aria-hidden="true" />
        </Button>
      </div>

      <!-- Form Content -->
      <div class={styles.formContainer} role="main">
        <BackInStockForm product={variantToProduct(product)} />
      </div>
    </div>
  </div>
{/if}
